import {
  downloadClassicRegistrationReport,
  ClassicRegistration,
} from '~src/services/api/modules';
import { DownloadMultipleClassicRegistrationsParams } from '~src/utils/downloader/types/types';
import { DownloadingState, useStore } from '~src/store';
import * as zip from '@zip.js/zip.js';
import streamSaver from 'streamsaver';
import { Modal } from 'antd';
import { MODULES } from '~src/types/enums';

// Helper function to get module names
const MODULE_NAMES: Record<number, string> = {
  [MODULES.TASK]: 'Tasks',
  [MODULES.TQ]: 'Enquiries',
  [MODULES.SUPV]: 'Supervision',
  [MODULES.CONT]: 'Control',
  [MODULES.COM]: 'Commissioning',
  [MODULES.ECO]: 'Economics',
  [MODULES.DV]: 'Facility Management',
  [MODULES.AFTS]: 'Contract',
};

function getModuleNameById(moduleId: number): string {
  return MODULE_NAMES[moduleId] || `Module_${moduleId}`;
}

// --- ZipStream Class with Folder Support ---
class ZipStream {
  private zipWriter: zip.ZipWriter<WritableStream<any>> | null = null;
  private writableStream: WritableStream<any> | null = null;
  public successLog: string[] = [];
  public errorLog: string[] = [];
  private fileMap: Map<string, number> = new Map();
  private currentFileAlreadyStreamed = 0;
  private totalFiles = 0;
  public totalSize = 0;
  private downloadedSize = 0;
  private startTime = 0;
  private endTime = 0;
  public isCancelled = false;

  constructor(totalFiles: number, totalSize: number) {
    this.totalFiles = totalFiles;
    this.totalSize = totalSize;
    useStore.getState().changeTotalFileSize(totalSize);
  }

  start() {
    try {
      useStore.getState().totalDownloadFiles(this.totalFiles);
      this.startTime = performance.now();
      this.writableStream = streamSaver.createWriteStream(
        `C-WEB-Registrations-${Date.now()}.zip`
      );
      this.zipWriter = new zip.ZipWriter(this.writableStream);
    } catch (error) {
      this.isCancelled = true;
    }
  }

  cancel() {
    this.isCancelled = true;
    this.writableStream = null;
    this.zipWriter = null;
    useStore.getState().changeDownloadingState(DownloadingState.FINISHING);
    setTimeout(() => useStore.getState().changeDownloadingActive(false), 3000);
  }

  getUniqueFileName(filePath: string): string {
    let uniquePath = filePath;
    let count = 1;
    const match = filePath.match(/^(.*?)(\(\d+\))?(\.[^/.]+)?$/);
    const baseName = match?.[1] || filePath;
    const extension = match?.[3] || '';
    while (this.fileMap.has(uniquePath)) {
      uniquePath = `${baseName}(${count})${extension}`;
      count++;
    }
    this.fileMap.set(uniquePath, count);
    return uniquePath;
  }

  async addFile(folder: string, name: string, blob: Blob) {
    if (this.isCancelled || !this.zipWriter) return;
    const uniqueName = this.getUniqueFileName(`${folder}${name}`);
    try {
      useStore.getState().changeDownloadingState(DownloadingState.FETCHING);
      await this.zipWriter.add(uniqueName, new zip.BlobReader(blob));
      this.currentFileAlreadyStreamed++;
      this.downloadedSize += blob.size;
      useStore.getState().currentDownloadFile(this.currentFileAlreadyStreamed);
      this.successLog.push(
        `${uniqueName} - success (${(blob.size / 1024 / 1024).toFixed(2)} MB)`
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      this.errorLog.push(
        `${uniqueName} (0.00 MB) - ERROR: ${errorMessage} | Classic Registrations With Folders`
      );
      useStore
        .getState()
        .addError(
          `${uniqueName} (0.00 MB) - ERROR: ${errorMessage} | Classic Registrations With Folders`
        );
      this.isCancelled = true;
    }
  }

  async finish() {
    try {
      if (!this.zipWriter || this.isCancelled) return;
      this.endTime = performance.now();
      const totalSizeMB = (this.downloadedSize / 1024 / 1024).toFixed(2);
      const timeTaken = (this.endTime - this.startTime) / 1000;
      const speed = (this.downloadedSize / 1024 / 1024 / timeTaken).toFixed(2);
      const totalErrors = this.errorLog.length;

      // Merge successes and errors in a structured way
      const mergedLogs: string[] = [];
      const successEntries = [...this.successLog];
      const errorEntries = [...this.errorLog];

      while (successEntries.length || errorEntries.length) {
        if (successEntries.length) mergedLogs.push(successEntries.shift()!);
        if (errorEntries.length) mergedLogs.push(errorEntries.shift()!);
      }

      // Construct final log content
      const finalLog = [
        ...mergedLogs,
        `\n==== Summary ====`,
        `Total files - ${this.totalFiles}`,
        `Total errors - ${totalErrors}`,
        `Total size - ${totalSizeMB} MB`,
        `Download speed - ${speed} MB/s`,
        `Time taken - ${timeTaken.toFixed(2)} s`,
      ];

      if (totalErrors > 0) {
        finalLog.push(`\n==== ${totalErrors} Errors ====`, ...this.errorLog);
      }

      const logBlob = new Blob([finalLog.join('\n')], { type: 'text/plain' });
      await this.zipWriter.add('download_log.txt', new zip.BlobReader(logBlob));
      await this.zipWriter.close();

      const clearPanel = () => {
        useStore.getState().changeDownloadingActive(false);
        useStore.getState().clearErrors();
      };

      useStore.getState().changeDownloadingState(DownloadingState.FINISHING);
    } catch (error) {
      this.isCancelled = true;
    }
  }
}

// --- Modal Confirm Download ---
const ModalConfirmDownload = (): Promise<boolean> => {
  function onCancelfunction() {
    useStore.getState().changeDownloadingState('FINISHING');
    setTimeout(() => useStore.getState().changeDownloadingActive(false), 1500);
  }
  return new Promise((resolve) => {
    Modal.confirm({
      title: 'Warning',
      content:
        'Too many registrations selected (50+ files). There can be problems with downloading. We recommend reducing total registrations to 50 max. at once.',
      onOk: () => resolve(true),
      onCancel: () => {
        onCancelfunction();
        resolve(false);
      },
      okText: 'I understand',
      cancelText: 'Cancel',
    });
  });
};

// --- Fixed-concurrency task pool (5 in-flight requests at a time, regardless
// of total selection size) - replaces the old chunk-of-10-only-if->50
// strategy, which allowed up to 50 concurrent requests to the backend. ---
const CONCURRENCY = 5;

async function processWithConcurrency(
  items: ClassicRegistration[],
  zipStream: ZipStream
): Promise<void> {
  const queue = [...items];

  const worker = async () => {
    while (queue.length > 0) {
      if (zipStream.isCancelled) return;
      const reg = queue.shift();
      if (!reg) return;

      const filename = reg.filename || `registration_${reg.id}.pdf`;
      const folderPath = `${getModuleNameById(reg.module)}/`;

      try {
        const blob = await downloadClassicRegistrationReport({
          projectId: String(reg.project_id),
          id: reg.id,
          response_type: 'stream',
        });
        await zipStream.addFile(folderPath, filename, blob);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        zipStream.errorLog.push(
          `${filename} (0.00 MB) - ERROR: ${errorMessage} | Classic Registrations With Folders`
        );
        useStore
          .getState()
          .addError(
            `${filename} (0.00 MB) - ERROR: ${errorMessage} | Classic Registrations With Folders`
          );
      }
    }
  };

  await Promise.all(Array.from({ length: CONCURRENCY }, () => worker()));
}

// --- Main Export Function ---
export const downloadMultipleClassicRegistrationsWithFolders = async ({
  classicRegistrations,
  onDownloadStarted,
}: DownloadMultipleClassicRegistrationsParams): Promise<void> => {
  useStore.getState().changeDownloadingState(DownloadingState.DOWNLOADING);

  let startDownload = true;
  if (classicRegistrations.length > 50) {
    startDownload = await ModalConfirmDownload();
  }
  if (!startDownload) return;

  const totalFiles = classicRegistrations.length;

  onDownloadStarted && onDownloadStarted();

  const zipStream = new ZipStream(totalFiles, 0);
  zipStream.start();
  if (zipStream.isCancelled) return;

  await processWithConcurrency(classicRegistrations, zipStream);

  if (!zipStream.isCancelled) {
    await zipStream.finish();
  }
};
